import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, DollarSign, Activity, BarChart3, HelpCircle, X } from 'lucide-react';
import type { EconomicIndicator, EconomicInsight } from '../types/economicIndicators';
import { economicIndicatorsService } from '../services/economicIndicators';

interface EconomicIndicatorsProps {
  className?: string;
}

const EconomicIndicators: React.FC<EconomicIndicatorsProps> = ({ className = '' }) => {
  const [indicators, setIndicators] = useState<EconomicIndicator[]>([]);
  const [insights, setInsights] = useState<EconomicInsight[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIndicator, setSelectedIndicator] = useState<string | null>(null);
  const [showSignalsHelp, setShowSignalsHelp] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [indicatorsData, insightsData] = await Promise.all([
          economicIndicatorsService.getEconomicIndicators(),
          economicIndicatorsService.generateMarketInsights()
        ]);
        setIndicators(indicatorsData);
        setInsights(insightsData);
      } catch (error) {
        console.error('Error loading economic indicators:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();

    // Set up real-time updates
    const interval = economicIndicatorsService.startRealTimeUpdates((data) => {
      setIndicators(data);
    });

    return () => {
      if (interval) clearInterval(interval);
    };
  }, []);

  const getIndicatorIcon = (symbol: string) => {
    switch (symbol) {
      case 'DXY':
        return <DollarSign className="w-5 h-5" />;
      case 'VX1':
        return <Activity className="w-5 h-5" />;
      case '10Y1':
      case 'ZN1':
        return <BarChart3 className="w-5 h-5" />;
      case 'USEPU':
        return <AlertTriangle className="w-5 h-5" />;
      default:
        return <TrendingUp className="w-5 h-5" />;
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'High':
        return 'text-red-200 bg-red-500/30';
      case 'Medium':
        return 'text-yellow-200 bg-yellow-500/30';
      case 'Low':
        return 'text-green-200 bg-green-500/30';
      default:
        return 'text-slate-300 bg-slate-500/30';
    }
  };

  const getDirectionColor = (direction: string) => {
    switch (direction) {
      case 'Bullish':
        return 'text-green-400';
      case 'Bearish':
        return 'text-red-400';
      case 'Neutral':
        return 'text-slate-400';
      default:
        return 'text-slate-400';
    }
  };

  const formatValue = (value: number, symbol: string) => {
    if (symbol === 'VX1' || symbol === 'USEPU') {
      return value.toFixed(2);
    }
    if (symbol === 'DXY') {
      return value.toFixed(2);
    }
    if (symbol === '10Y1') {
      return `${value.toFixed(3)}%`;
    }
    if (symbol === 'ZN1') {
      return value.toFixed(2);
    }
    return value.toFixed(2);
  };

  if (loading) {
    return (
      <div className={`bg-gradient-to-br from-slate-900 to-slate-800 rounded-lg shadow-lg border border-slate-700 p-6 ${className}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Economic Indicators</h3>
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-400"></div>
        </div>
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="h-16 bg-slate-700/50 rounded-lg"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-gradient-to-br from-slate-900 to-slate-800 rounded-lg shadow-lg border border-slate-700 ${className}`}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white">Economic Indicators</h3>
          <span className="text-sm text-slate-400">
            Updated: {new Date().toLocaleTimeString()}
          </span>
        </div>

        {/* Instructions for user */}
        <div className="mb-4 p-3 bg-blue-500/20 border border-blue-400/30 rounded-lg">
          <p className="text-sm text-blue-200">
            💡 <strong>How to read data:</strong> Click on any indicator to see correlations with carousel assets and impact on futures markets.
          </p>
        </div>

        {/* Indicators Grid */}
        <div className="space-y-3 mb-6">
          {indicators.map((indicator) => (
            <div
              key={indicator.symbol}
              className={`p-4 rounded-lg border transition-all duration-200 cursor-pointer hover:shadow-lg ${
                selectedIndicator === indicator.symbol
                  ? 'border-blue-400 bg-blue-500/10 shadow-blue-500/20'
                  : 'border-slate-600 hover:border-slate-500 bg-slate-800/50 hover:bg-slate-700/50'
              }`}
              onClick={() => setSelectedIndicator(
                selectedIndicator === indicator.symbol ? null : indicator.symbol
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${getImpactColor(indicator.impact)}`}>
                    {getIndicatorIcon(indicator.symbol)}
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-white">{indicator.symbol}</span>
                      <span className={`px-2 py-1 text-xs rounded-full ${getImpactColor(indicator.impact)}`}>
                        {indicator.impact}
                      </span>
                    </div>
                    <p className="text-sm text-slate-300">{indicator.name}</p>
                  </div>
                </div>
                <div className="text-right">
                    <div className="flex items-center space-x-2">
                      <span className="text-lg font-semibold text-white">
                        {formatValue(indicator.value, indicator.symbol)}
                      </span>
                    <div className={`flex items-center space-x-1 ${getDirectionColor(indicator.direction)}`}>
                      {indicator.changePercent > 0 ? (
                        <TrendingUp className="w-4 h-4" />
                      ) : indicator.changePercent < 0 ? (
                        <TrendingDown className="w-4 h-4" />
                      ) : null}
                      <span className="text-sm font-medium">
                        {indicator.changePercent > 0 ? '+' : ''}{indicator.changePercent.toFixed(2)}%
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">{indicator.direction}</p>
                </div>
              </div>
              
              {/* Expanded Details */}
              {selectedIndicator === indicator.symbol && (
                <div className="mt-4 pt-4 border-t border-slate-600">
                  <p className="text-sm text-slate-300 mb-3">{indicator.description}</p>
                  
                  {/* Impact on Futures Markets */}
                  <div className="mb-4 p-3 bg-orange-500/20 border border-orange-400/30 rounded-lg">
                    <h5 className="text-sm font-semibold text-orange-200 mb-2">
                      📈 Impact on Futures Markets
                    </h5>
                    <p className="text-sm text-orange-100">
                      {indicator.symbol === 'DXY' && 'A strong dollar reduces prices of USD-denominated commodities (oil, gold, metals).'}
                      {indicator.symbol === 'VX1' && 'High volatility increases futures premiums and reduces risk appetite.'}
                      {indicator.symbol === '10Y1' && 'High yields strengthen the dollar and pressure commodity prices.'}
                      {indicator.symbol === 'ZN1' && 'High bond prices indicate flight-to-quality, negative for risky commodities.'}
                      {indicator.symbol === 'USEPU' && 'High uncertainty increases volatility and reduces commodity investments.'}
                    </p>
                  </div>

                  {/* Correlations with Carousel Assets */}
                  <div className="mb-4">
                    <h5 className="text-sm font-semibold text-slate-200 mb-3">
                      🔗 Correlation with Carousel Assets
                    </h5>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {indicator.correlations.currencies.length > 0 && (
                        <div>
                          <h6 className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">
                            Currencies
                          </h6>
                          <div className="flex flex-wrap gap-1">
                            {indicator.correlations.currencies.slice(0, 4).map((curr) => (
                              <span key={curr} className="px-2 py-1 text-xs bg-blue-500/30 text-blue-200 rounded">
                                {curr}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {indicator.correlations.commodities.length > 0 && (
                        <div>
                          <h6 className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">
                            Commodities (Carousel)
                          </h6>
                          <div className="flex flex-wrap gap-1">
                            {indicator.correlations.commodities.slice(0, 4).map((comm) => (
                              <span key={comm} className="px-2 py-1 text-xs bg-yellow-500/30 text-yellow-200 rounded">
                                {comm === 'CRUDE_OIL' ? 'CRUDE OIL' : comm === 'GASOLINE' ? 'GASOLINE' : comm}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {indicator.correlations.indices.length > 0 && (
                        <div>
                          <h6 className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">
                            Indices
                          </h6>
                          <div className="flex flex-wrap gap-1">
                            {indicator.correlations.indices.slice(0, 4).map((idx) => (
                              <span key={idx} className="px-2 py-1 text-xs bg-green-500/30 text-green-200 rounded">
                                {idx}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Market Signals - Always Visible */}
        <div className="border-t border-slate-600 pt-6">
          <div className="flex items-center mb-4">
            <div className="p-2 bg-purple-500/30 rounded-lg mr-3">
              <BarChart3 className="w-5 h-5 text-purple-300" />
            </div>
            <h4 className="text-lg font-semibold text-white">🎯 Market Signals</h4>
            <button
              onClick={() => setShowSignalsHelp(true)}
              className="ml-auto p-1 text-slate-400 hover:text-blue-400 transition-colors"
              title="How to use Market Signals"
            >
              <HelpCircle className="w-4 h-4" />
            </button>
          </div>
          {insights.length > 0 ? (
            <div className="space-y-3">
              {insights.map((insight, index) => (
                <div key={index} className={`p-4 rounded-lg border-l-4 hover:shadow-lg transition-shadow ${
                  insight.impact === 'Positive' ? 'bg-green-500/20 border-green-400' :
                  insight.impact === 'Negative' ? 'bg-red-500/20 border-red-400' :
                  'bg-slate-700/50 border-slate-400'
                }`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <h5 className="font-semibold text-white mr-2">{insight.title}</h5>
                        <span className={`px-3 py-1 text-xs font-bold rounded-full border ${
                          insight.impact === 'Positive' ? 'bg-green-500/30 text-green-200 border-green-300' :
                          insight.impact === 'Negative' ? 'bg-red-500/30 text-red-200 border-red-300' :
                          'bg-slate-500/30 text-slate-200 border-slate-300'
                        }`}>
                          {insight.impact === 'Positive' ? '📈 BULLISH' : insight.impact === 'Negative' ? '📉 BEARISH' : '⏸️ NEUTRAL'}
                        </span>
                      </div>
                      <p className="text-sm text-slate-300 mb-3">{insight.description}</p>
                      <div className="flex items-center space-x-6 text-xs">
                        <div className="flex items-center">
                          <span className="text-slate-400 mr-1">Confidence:</span>
                          <span className={`font-bold px-2 py-1 rounded ${
                            insight.confidence >= 80 ? 'text-green-200 bg-green-500/30' :
                            insight.confidence >= 60 ? 'text-yellow-200 bg-yellow-500/30' :
                            'text-red-200 bg-red-500/30'
                          }`}>
                            {insight.confidence}%
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-slate-400 mr-1">📅 Timeframe:</span>
                          <span className="font-medium text-slate-300 bg-slate-600/30 px-2 py-1 rounded">{insight.timeframe}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-slate-400 mr-1">Action:</span>
                          <span className={`font-bold px-3 py-1 rounded-full ${
                            insight.impact === 'Positive' ? 'bg-green-600 text-white' :
                            insight.impact === 'Negative' ? 'bg-red-600 text-white' :
                            'bg-slate-600 text-white'
                          }`}>
                            {insight.impact === 'Positive' ? '📈 BUY' : insight.impact === 'Negative' ? '📉 SELL' : '⏸️ HOLD'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400 mx-auto mb-3"></div>
              <p className="text-sm text-slate-400">Loading market signals...</p>
            </div>
          )}
        </div>
      </div>

      {/* Market Signals Help Popup */}
      {showSignalsHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                  <BarChart3 className="w-6 h-6 text-blue-600" />
                  How to Use Market Signals
                </h2>
                <button
                  onClick={() => setShowSignalsHelp(false)}
                  className="p-1 text-slate-500 hover:text-slate-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4 text-slate-700">
                <div>
                  <h3 className="font-semibold text-slate-800 mb-2">📊 Signal Components</h3>
                  <ul className="space-y-2 ml-4">
                    <li><strong>Impact:</strong> BULLISH (buy signal), BEARISH (sell signal), NEUTRAL (hold/wait)</li>
                    <li><strong>Confidence:</strong> 60-70% (low), 70-80% (medium), 80%+ (high confidence)</li>
                    <li><strong>Timeframe:</strong> When to expect the move (short/medium/long-term)</li>
                    <li><strong>Action:</strong> BUY, SELL, or HOLD recommendation</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold text-slate-800 mb-2">🎯 Target Assets</h3>
                  <p className="mb-2">Each signal specifies which futures from the carousel to trade:</p>
                  <ul className="space-y-1 ml-4">
                    <li><strong>Energy:</strong> CRUDE OIL, GASOLINE</li>
                    <li><strong>Metals:</strong> GOLD, SILVER, COPPER</li>
                    <li><strong>All Commodities:</strong> When USD or volatility affects entire complex</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold text-slate-800 mb-2">⚡ How to Act</h3>
                  <div className="space-y-2">
                    <div className="p-3 bg-green-50 rounded border-l-4 border-green-400">
                      <strong className="text-green-800">BULLISH Signal:</strong> Look for BUY opportunities in the specified futures. Enter long positions.
                    </div>
                    <div className="p-3 bg-red-50 rounded border-l-4 border-red-400">
                      <strong className="text-red-800">BEARISH Signal:</strong> Look for SELL opportunities in the specified futures. Enter short positions or exit longs.
                    </div>
                    <div className="p-3 bg-yellow-50 rounded border-l-4 border-yellow-400">
                      <strong className="text-yellow-800">NEUTRAL Signal:</strong> HOLD current positions. Wait for clearer directional signals.
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold text-slate-800 mb-2">⚠️ Risk Management</h3>
                  <ul className="space-y-1 ml-4">
                    <li>Higher confidence signals (80%+) warrant larger position sizes</li>
                    <li>Always use stop-losses appropriate for the timeframe</li>
                    <li>Consider correlations: USD strength affects all commodities</li>
                    <li>Monitor the carousel futures for technical confirmation</li>
                  </ul>
                </div>
                
                <div className="p-4 bg-blue-50 rounded border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <strong>💡 Pro Tip:</strong> These signals are based on economic indicators and their historical correlations with futures markets. 
                    Always combine with technical analysis and your risk management rules.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EconomicIndicators;